// @ts-strict-ignore
import {Injectable, signal} from "@angular/core";
import {NetworkAudioInputChannel} from "@models";
import {AudioDevice, IAudioClientService} from "./i-audio-client.service";
import {timer} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class NetworkAudioClientService implements IAudioClientService {
	public readonly connected = signal<boolean>(false);
	public readonly selectedAudioDevice = signal<AudioDevice>(null);
	public readonly audioDevices = signal<AudioDevice[]>([]);
	public readonly muted = signal<boolean>(false);
	public readonly volume = signal<number>(1);
	public readonly supportsVisualizer = false;
	public readonly supportsVolumeControl = false;

	private readonly LocalAudioClientEndpoint: string = "http://localhost:38080";

	constructor() {
		timer(0, 5000)
			.pipe(untilDestroyed(this))
			.subscribe(async () => await this.checkConnection());
	}

	private async checkConnection(): Promise<void> {
		await this.fetch<boolean>(this.buildEndpoint("Ping"))
			.catch<boolean>(() => false)
			.then((x) => this.connected.set(x));
	}

	async reloadAudioDevices(): Promise<void> {
		this.audioDevices.set(await this.getAudioDevices());
		this.selectedAudioDevice.set(await this.getSelectedAudioDevice());
	}

	private async getSelectedAudioDevice(): Promise<AudioDevice> {
		let device = await this.fetch<string>(this.buildEndpoint("GetSelectedAudioDevice")).catch<string>(() => "");
		return new AudioDevice(device, device, device);
	}

	private async getAudioDevices(): Promise<AudioDevice[]> {
		let devices = await this.fetch<string[]>(this.buildEndpoint("GetAudioDevices"))
			.catch<string[]>(() => [])
			.then((x) => x.sort());
		return devices.map((device) => {
			return new AudioDevice(device, device, device);
		});
	}

	async setAudioDevice(audioDevice: AudioDevice): Promise<boolean> {
		let success = await this.fetch<boolean>(this.buildEndpoint("SetAudioDevice"), {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify([
				{
					key: "deviceName",
					value: JSON.stringify(audioDevice?.deviceId),
				},
			]),
		}).catch<boolean>(() => false);

		await this.reloadAudioDevices();
		return success;
	}

	private buildEndpoint(method: string): string {
		return `${this.LocalAudioClientEndpoint}/nmp/${method}`;
	}

	private async fetch<T>(url: string, options?: RequestInit): Promise<T> {
		let response = await fetch(url, options);

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		return (await response.json()) as T;
	}

	async startSending(_: NetworkAudioInputChannel): Promise<boolean> {
		return false;
	}

	async startVisualizer(_: HTMLCanvasElement): Promise<boolean> {
		return false;
	}

	async stopSending(): Promise<boolean> {
		return false;
	}

	async stopVisualizer(): Promise<boolean> {
		return false;
	}
}
